import {printoutPalette} from "../../core/printoutPalette";
import {containerBlockSx} from "../container/containerBlockStyles";
import {dataListBlockSx} from "../dataList/dataListBlockStyles";
import {dividerBlockSx} from "../divider/dividerBlockStyles";
import {fieldBlockSx} from "../field/fieldBlockStyles";
import {
    editorPrintoutRegionChromeSx,
    printoutRegionBlockSx,
} from "../headerFooter/regionBlockStyles";
import {imageBlockSx} from "../image/imageBlockStyles";
import {pageBreakBlockSx} from "../pageBreak/pageBreakBlockStyles";
import {tableBlockSx} from "../table/tableBlockStyles";
import {writingAreaBlockSx} from "../writingArea/writingAreaBlockStyles";

// Shared document styles composed from each block's local style fragment.
export const sharedPrintoutBlockBodySx = {
    '& .printout-block-content': {
        boxSizing: 'border-box',
        color: printoutPalette.secondaryText,
        maxWidth: 'none',
        position: 'relative',
        width: '100%',
    },
    ...containerBlockSx,
    ...dataListBlockSx,
    ...dividerBlockSx,
    ...fieldBlockSx,
    ...imageBlockSx,
    ...pageBreakBlockSx,
    ...printoutRegionBlockSx,
    ...tableBlockSx,
    ...writingAreaBlockSx,
};

export const editorPrintoutBlockChromeSx = {
    ...editorPrintoutRegionChromeSx,
    '& .printout-block-shell': {
        '--printout-block-outline-idle-opacity': 0,
        borderRadius: 2,
        boxSizing: 'border-box',
        boxShadow: 'none',
        my: 2,
        overflow: 'visible',
        position: 'relative',
    },
    '& .printout-block-shell[data-printout-block-type="printoutContainer"]': {
        '--printout-block-outline-idle-opacity': 0.4,
    },
    '& .printout-block-shell[data-printout-block-type="printoutContainer"] > .printout-block-dash rect': {
        strokeDasharray: 'none',
    },
    '& .printout-block-dash': {
        color: printoutPalette.border,
        // Drag feedback reveals chrome without changing selection or opening every block menu.
        opacity: 'var(--printout-drag-chrome-opacity, var(--printout-block-outline-idle-opacity))',
        pointerEvents: 'none',
        transition: 'color 120ms ease, opacity 120ms ease',
    },
    '& .printout-block-shell:hover > .printout-block-dash': {
        opacity: 1,
    },
    '& .printout-block-shell:hover > .printout-block-dash rect': {
        strokeDasharray: 'none',
    },
    '& .printout-block-shell.is-selected > .printout-block-dash, & .ProseMirror-selectednode.printout-block-shell > .printout-block-dash': {
        color: 'primary.main',
        opacity: 1,
    },
    '& .printout-block-shell.is-selected > .printout-block-dash rect, & .ProseMirror-selectednode.printout-block-shell > .printout-block-dash rect': {
        strokeDasharray: 'none',
    },
    '& .printout-block-shell:has(.printout-block-shell:hover):not(.is-selected):not(.ProseMirror-selectednode) > .printout-block-dash': {
        opacity: 'var(--printout-drag-chrome-opacity, var(--printout-block-outline-idle-opacity))',
    },
    '& .printout-block-shell[data-printout-block-type="printoutDivider"] > .printout-block-content::before': {
        bottom: '-9px',
        content: '""',
        left: 0,
        position: 'absolute',
        right: 0,
        top: '-9px',
    },
    // Absolute chrome must not change pagination's page-break fill height.
    '& .printout-block-shell[data-printout-block-type="printoutPageBreak"] .printout-page-break-rule': {
        alignItems: 'center',
        borderTop: '1px dashed',
        borderTopColor: 'primary.main',
        display: 'flex',
        justifyContent: 'center',
        left: 0,
        position: 'absolute',
        right: 0,
        top: 0,
    },
    '& .printout-block-shell[data-printout-block-type="printoutPageBreak"] .printout-page-break-label': {
        bgcolor: 'primary.main',
        borderRadius: 999,
        color: 'primary.contrastText',
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '0.06em',
        lineHeight: 1.6,
        px: 0.75,
        textTransform: 'uppercase',
        transform: 'translateY(-50%)',
        userSelect: 'none',
        whiteSpace: 'nowrap',
    },
    '& .printout-container-columns[data-printout-container-separators="false"]': {
        "--printout-column-separator": `1px dashed ${printoutPalette.subtleBorder}`,
    },
    '& .printout-block-menu': {
        bgcolor: 'background.paper',
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        boxShadow: '0 16px 34px rgba(15, 23, 42, 0.14)',
        flexWrap: 'wrap',
        left: 10,
        maxWidth: 'var(--crqlar-printout-page-width)',
        p: 0.5,
        position: 'absolute',
        top: -38,
        transform: 'translateX(var(--printout-menu-shift-x, 0px))',
        zIndex: 10,
    },
    '& .printout-block-menu[data-printout-menu-placement="below"]': {
        bottom: 'auto',
        top: 'calc(100% + 2px)',
    },
    '& .printout-block-drag-handle': {
        alignItems: 'center',
        color: 'text.disabled',
        cursor: 'grab',
        display: 'flex',
        height: 28,
        justifyContent: 'center',
        userSelect: 'none',
        width: 28,
    },
    '& .printout-block-gutter-handle': {
        left: -32,
        opacity: 'var(--printout-drag-chrome-opacity, 0)',
        position: 'absolute',
        top: 0,
        transition: 'opacity 120ms ease',
        zIndex: 2,
    },
    '& .printout-block-shell:hover > .printout-block-gutter-handle, & .printout-block-shell.is-selected > .printout-block-gutter-handle': {
        opacity: 1,
    },
    '& .printout-block-shell:has(.printout-block-shell:hover):not(.is-selected):not(.ProseMirror-selectednode) > .printout-block-gutter-handle': {
        opacity: 'var(--printout-drag-chrome-opacity, 0)',
    },
    '& .printout-block-gutter-handle:active': {
        cursor: 'grabbing',
    },
    '& .printout-block-name-label': {
        alignItems: 'center',
        color: 'text.primary',
        display: 'inline-flex',
        fontSize: 13,
        fontWeight: 600,
        gap: 0.75,
        height: 28,
        px: 0.75,
        userSelect: 'none',
        whiteSpace: 'nowrap',
    },
    '& .printout-block-name-label-icon': {
        alignItems: 'center',
        color: 'primary.main',
        display: 'inline-flex',
    },
    '& .printout-block-menu-icon': {
        borderRadius: 1,
        color: 'text.secondary',
        height: 28,
        width: 28,
    },
    '& .printout-block-quick-actions': {
        alignItems: 'center',
        display: 'inline-flex',
        gap: 0.25,
        '& .MuiToggleButtonGroup-root': {
            height: 28,
        },
        '& .MuiToggleButton-root': {
            borderColor: 'transparent',
            borderRadius: 1,
            color: 'text.secondary',
            height: 28,
            minWidth: 28,
            p: 0,
        },
        '& .MuiToggleButton-root.Mui-selected': {
            bgcolor: 'action.selected',
            color: 'primary.main',
        },
    },
};

export const previewPrintoutBlockChromeSx = {
    '& .printout-block-shell': {
        borderRadius: 1.5,
        boxSizing: 'border-box',
        my: 2,
        pointerEvents: 'none',
        position: 'relative',
    },
    '& .printout-block-shell *': {
        pointerEvents: 'none',
    },
    '& .printout-block-menu': {
        display: 'none',
    },
};
